import el from "@cypherpotato/el";
import { Badge, BadgeList, TagBadge } from "../../components/badge";
import { Button, ButtonRow } from "../../components/button";
import { Card, CardContent, CardGrid } from "../../components/card";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { FormControl, Select, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { Row, Section } from "../../components/layout";
import { MultilingualCodeWrapper, MutedText, Paragraph } from "../../components/text";
import { displayAlert } from "../../ui/alert";
import { copyToClipboard } from "../../ui/clipboard";
import { cond } from "../../util";
import { getUniqueColor } from "../../ui/unique-color";
import { showFlyout } from "../../ui/flyout";
import { throttledCall } from "../../ui/throttle";
import { runOperation } from "../../ui/operation";

const InstructionStoreCollectionId = "019ecd98-6c22-7e0b-981e-e8f73eb19401";
const InstructionStoreDefaultQuery = "Instruction";

function downloadText(filename, text) {
    const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}

async function loadMarkdown(item) {
    return item.markdown;
}

function InstructionCard(item) {
    async function viewMarkdown() {
        const markdown = await loadMarkdown(item);

        displayAlert({
            title: item.title,
            dialogSize: "large",
            message: MultilingualCodeWrapper(item.path.split("/").pop(), [
                {
                    label: "Markdown",
                    language: "markdown",
                    code: markdown
                }
            ])
        });
    }

    return el("div.instruction-store-card",
        el("div.instruction-store-card-content",
            el(".instruction-store-card-heading",
                cond(
                    [item.type == "instruction", el(".instruction-store-card-tag",
                        Icon("settings-3-line"),
                        "Instruction"
                    )],
                    [item.type == "skill", el(".instruction-store-card-tag",
                        Icon("user-settings-line"),
                        "Skill"
                    )],
                    [item.type == "workflow", el(".instruction-store-card-tag",
                        Icon("network-line"),
                        "Workflow"
                    )],
                ),
                ButtonRow(
                    Button(Icon("eye-line"), "View", {
                        onClick() {
                            viewMarkdown();
                        }
                    }),
                    Dropdown(
                        null,
                        DropdownItem("Download", Icon("download-line"), {
                            async onClick() {
                                downloadText(item.filename, await loadMarkdown(item));
                            }
                        }),
                        DropdownItem("Copy markdown", Icon("file-copy-line"), {
                            async onClick() {
                                copyToClipboard(await loadMarkdown(item));
                            }
                        })
                    )
                ),
            ),
            el(".instruction-store-card-header",
                Icon(item.icon.replace(/^ri-/, '')),
                el("h3", item.title),
            ),
            Paragraph(item.description),
            el(".instruction-store-tags",
                item.tags.slice(0, 4).map(tag => TagBadge(tag, { style: { color: getUniqueColor(tag) } }))
            )
        )
    );
}

export default function InstructionStore() {
    const items = el.state.create(null);
    const query = el.state.create("");
    const type = el.state.create("all");
    const category = el.state.create("all");
    const error = el.state.create(null);
    let requestId = 0;

    function queryTerm() {
        return query.value.trim() || InstructionStoreDefaultQuery;
    }

    function mapResult(result) {
        const metadata = result.metadata || {};
        const tags = metadata.categories || metadata.tags || [];
        const documentName = result.documentName || metadata.slug || metadata.title || "instruction-store/document";
        const filename = metadata.filename || `${documentName.split("/").pop()}.md`;

        return {
            title: metadata.title || documentName,
            description: metadata.description || "",
            icon: metadata.icon || "ri-file-text-line",
            type: metadata.type || "instruction",
            tags,
            path: metadata.path || documentName,
            slug: metadata.slug || documentName.replace(/^instruction-store\//, ""),
            filename,
            markdown: result.documentContent || "",
            score: result.score
        };
    }

    async function loadItems() {
        await runOperation("Loading instructions...", async () => {
            const currentRequestId = ++requestId;
            const publicLabsKey = window.env?.PublicLabsKey;

            items.value = null;
            error.value = null;

            if (!publicLabsKey) {
                items.value = [];
                error.value = "Instruction Store is unavailable.";
                return;
            }

            try {
                const response = await fetch("https://inference.aivax.net/api/v1/query", {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${publicLabsKey}`,
                        "Content-Type": "text/plain;charset=UTF-8"
                    },
                    body: JSON.stringify({
                        term: queryTerm(),
                        collections: [
                            InstructionStoreCollectionId
                        ],
                        minScore: 0.4,
                        top: 100,
                        includeReferences: true
                    })
                });

                if (!response.ok) {
                    throw new Error("Failed to load instructions.");
                }

                const responseData = await response.json();
                const documents = Array.isArray(responseData) ? responseData : responseData.data || [];

                if (currentRequestId === requestId) {
                    items.value = documents.map(mapResult);
                }
            } catch {
                if (currentRequestId === requestId) {
                    items.value = [];
                    error.value = "Instruction Store is unavailable.";
                }
            }
        });
    }

    loadItems();

    function filteredItems() {
        return (items.value || []).filter(item => {
            if (type.value !== "all" && item.type !== type.value) {
                return false;
            }

            if (category.value !== "all" && !(item.tags || []).includes(category.value)) {
                return false;
            }

            return true;
        });
    }

    function categoryOptions() {
        return [...new Set((items.value || []).flatMap(item => item.tags || []))]
            .sort((a, b) => a.localeCompare(b));
    }

    return Dashboard(
        DashboardTitle(
            "Instruction Store",
            "Free hosted instructions, skills, and workflows for agents."
        ),
        Section(
            CommandBar(
                FormControl(
                    "Search",
                    Row({ formSlot: true },
                        TextInput(null, "Search by title, tag, or type", {
                            onInput(event) {
                                query.value = event.target.value;
                                throttledCall(loadItems, 500);
                            }
                        }),
                        Button(Icon("price-tag-3-line"), {
                            onClick() {
                                showFlyout(
                                    Paragraph("Click on a tag to filter instructions by that category."),
                                    el.state.listening([items, category], () =>
                                        Paragraph(
                                            BadgeList(
                                                {
                                                    style: {
                                                        maxWidth: "400px"
                                                    }
                                                },
                                                categoryOptions().map(tag =>
                                                    Badge(tag, null, {
                                                        onClick() {
                                                            category.value = category.value === tag ? "all" : tag;
                                                        },
                                                        dataIsChecked: category.value === tag,
                                                        style: {
                                                            opacity: category.value === tag ? "1" : ".4",
                                                            cursor: "pointer"
                                                        }
                                                    })
                                                )
                                            )
                                        )
                                    )
                                );
                            }
                        })
                    )
                ),
                FormControl(
                    "Type",
                    Select({
                        value: "all",
                        items: [
                            { value: "all", text: "All" },
                            { value: "instruction", text: "Instruction" },
                            { value: "skill", text: "Skill" },
                            { value: "workflow", text: "Workflow" }
                        ],
                        onChange(event) {
                            type.value = event.target.value;
                        }
                    })
                )
            )
        ),
        Section(
            el.state.listening([items, type, category, error], () => {
                const visibleItems = filteredItems();

                if (!items.value) {
                    return MutedText("Loading instructions...");
                }

                if (!visibleItems.length) {
                    return MutedText(error.value || "No instructions found.");
                }

                return el(".instruction-store-grid",
                    ...visibleItems.map(InstructionCard)
                );
            })
        )
    );
}
